import { useState, useEffect, useRef, useCallback } from "react";
import { sortProfiles } from "@/shared/lib/profileSort";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Send,
  Loader2,
  X,
  Users,
  ChevronDown,
  Paperclip,
  File,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import { useMessages } from "@/shared/hooks/useMessages";
import { useToast } from "@/shared/hooks/use-toast";
import HelpTooltip from "@/shared/components/HelpTooltip";
import { cn } from "@/shared/lib/utils";
import {
  getAccessTokenForEdge,
  getSupabaseEdgeFunctionHeaders,
  getSupabaseFunctionsBaseUrl,
} from "@/shared/lib/supabaseFunctions";
import { uploadParaR2 } from "@/shared/lib/r2Storage";
import type { Database } from "@/shared/integrations/supabase/types";

interface Profile {
  id: string;
  user_id: string;
  nome: string;
  qualificacao: string;
}

type VinculoTipoDb = "judicial" | "administrativo" | "extrajudicial" | "procedimento";

interface ProcessoOpt {
  id: string;
  label: string;
}

interface AttachmentItem {
  id: string;
  file: File;
}

const VINCULO_LABELS: Record<VinculoTipoDb, string> = {
  judicial: "Judicial",
  administrativo: "Administrativo",
  extrajudicial: "Extrajudicial",
  procedimento: "Procedimento",
};

const MAX_ANEXOS = 10;
const MAX_BYTES_ANEXO = 20 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileTypeIcon(file: File) {
  const t = file.type || "";
  if (t.startsWith("image/")) return ImageIcon;
  if (t.includes("pdf")) return FileText;
  return File;
}

/** Upload R2: presign + PUT com JWT (getAccessTokenForEdge); fallback uploadParaR2 se PUT falhar (ex.: CORS). */
async function uploadAnexoCorreio(file: File, prefixo: string): Promise<string | null> {
  const ct = file.type || "application/octet-stream";
  try {
    const base = getSupabaseFunctionsBaseUrl();
    const token = await getAccessTokenForEdge();
    if (!base || !token) throw new Error("Sessão inválida");
    const presignRes = await fetch(`${base}/functions/v1/upload-r2`, {
      method: "POST",
      headers: {
        ...getSupabaseEdgeFunctionHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "presign-upload",
        modulo: "sistema",
        prefixo,
        originalName: file.name,
        contentType: ct,
        size: file.size,
      }),
    });
    const pj = (await presignRes.json().catch(() => ({}))) as {
      presignedUrl?: string;
      key?: string;
      error?: string;
    };
    if (!presignRes.ok || !pj.presignedUrl || !pj.key) {
      throw new Error(pj.error || "Falha ao preparar upload");
    }
    const put = await fetch(pj.presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": ct },
      body: file,
    });
    if (!put.ok) throw new Error(`Upload R2 (${put.status})`);
    return pj.key;
  } catch {
    try {
      const r = await uploadParaR2({
        file,
        modulo: "sistema",
        prefixo,
        contentType: ct,
      });
      return r.key;
    } catch {
      return null;
    }
  }
}

async function loadProcessosPorTipo(tipo: VinculoTipoDb): Promise<ProcessoOpt[]> {
  if (tipo === "judicial") {
    const { data, error } = await supabase
      .from("processos")
      .select("id, numero_processo, titulo")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) return [];
    return (data ?? []).map((row) => ({
      id: row.id,
      label: `${row.numero_processo || "—"} · ${(row.titulo || "").slice(0, 48)}${(row.titulo?.length ?? 0) > 48 ? "…" : ""}`,
    }));
  }
  if (tipo === "administrativo") {
    const { data, error } = await supabase
      .from("processos_administrativos")
      .select("id, numero, titulo")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) return [];
    return (data ?? []).map((row) => ({
      id: row.id,
      label: `${row.numero || "—"} · ${(row.titulo || "").slice(0, 48)}${(row.titulo?.length ?? 0) > 48 ? "…" : ""}`,
    }));
  }
  if (tipo === "extrajudicial") {
    try {
      const { data, error } = await supabase
        .from("processos_extrajudiciais")
        .select("id, numero, titulo")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) return [];
      return (data ?? []).map((row: { id: string; numero: string | null; titulo: string | null }) => ({
        id: row.id,
        label: `${row.numero || "—"} · ${(row.titulo || "").slice(0, 48)}${(row.titulo?.length ?? 0) > 48 ? "…" : ""}`,
      }));
    } catch {
      return [];
    }
  }
  try {
    const { data, error } = await supabase
      .from("procedimentos")
      .select("id, titulo, tipo")
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) return [];
    return (data ?? []).map((row: { id: string; titulo: string | null; tipo: string | null }) => ({
      id: row.id,
      label: `${row.titulo || "—"}${row.tipo ? ` (${row.tipo})` : ""}`.slice(0, 80),
    }));
  } catch {
    return [];
  }
}

async function fetchProcessoUnico(tipo: VinculoTipoDb, id: string): Promise<ProcessoOpt | null> {
  try {
    if (tipo === "judicial") {
      const { data, error } = await supabase
        .from("processos")
        .select("id, numero_processo, titulo")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) return null;
      return {
        id: data.id,
        label: `${data.numero_processo || "—"} · ${data.titulo || ""}`.slice(0, 80),
      };
    }
    if (tipo === "administrativo") {
      const { data, error } = await supabase
        .from("processos_administrativos")
        .select("id, numero, titulo")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) return null;
      return {
        id: data.id,
        label: `${data.numero || "—"} · ${data.titulo || ""}`.slice(0, 80),
      };
    }
    if (tipo === "extrajudicial") {
      const { data, error } = await supabase
        .from("processos_extrajudiciais")
        .select("id, numero, titulo")
        .eq("id", id)
        .maybeSingle();
      if (error || !data) return null;
      return {
        id: data.id,
        label: `${data.numero || "—"} · ${data.titulo || ""}`.slice(0, 80),
      };
    }
    const { data, error } = await supabase
      .from("procedimentos")
      .select("id, titulo, tipo")
      .eq("id", id)
      .maybeSingle();
    if (error || !data) return null;
    return {
      id: data.id,
      label: `${data.titulo || "—"}${data.tipo ? ` (${data.tipo})` : ""}`.slice(0, 80),
    };
  } catch {
    return null;
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultRecipients?: string[];
  defaultSubject?: string;
  defaultMessage?: string;
  dialogTitle?: string;
  defaultVinculoTipo?: string;
  defaultVinculoId?: string;
  /** Modo encaminhar: preenche messageId para tracking */
  encaminharDeId?: string;
}

const ComposeMessageDialog = ({
  open,
  onOpenChange,
  defaultRecipients,
  defaultSubject,
  defaultMessage,
  dialogTitle = "Nova Mensagem",
  defaultVinculoTipo,
  defaultVinculoId,
  encaminharDeId,
}: Props) => {
  const { user } = useAuth();
  const { sendMessage } = useMessages();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultRecipients || []);
  const [subject, setSubject] = useState(defaultSubject || "");
  const [message, setMessage] = useState(defaultMessage || "");
  const [senderName, setSenderName] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");

  const [vinculoPanelOpen, setVinculoPanelOpen] = useState(false);
  const [vinculoEnabled, setVinculoEnabled] = useState(false);
  const [vinculoTipo, setVinculoTipo] = useState<VinculoTipoDb>("judicial");
  const [vinculoId, setVinculoId] = useState<string>("");
  const [processosOpts, setProcessosOpts] = useState<ProcessoOpt[]>([]);
  const [loadingProcessos, setLoadingProcessos] = useState(false);

  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [uploadingAnexos, setUploadingAnexos] = useState(false);

  const refreshProcessos = useCallback(async (tipo: VinculoTipoDb, ensureId?: string) => {
    setLoadingProcessos(true);
    let list = await loadProcessosPorTipo(tipo);
    if (ensureId && !list.some((o) => o.id === ensureId)) {
      const one = await fetchProcessoUnico(tipo, ensureId);
      if (one) list = [one, ...list];
    }
    setProcessosOpts(list);
    setLoadingProcessos(false);
  }, []);

  useEffect(() => {
    if (!open) {
      setAttachments([]);
      setUploadingAnexos(false);
      setVinculoPanelOpen(false);
      setVinculoEnabled(false);
      setVinculoId("");
      setVinculoTipo("judicial");
      setProcessosOpts([]);
      return;
    }

    setSelectedIds(defaultRecipients || []);
    setSubject(defaultSubject || "");
    setMessage(defaultMessage || "");

    const dvTipo = defaultVinculoTipo as VinculoTipoDb | undefined;
    const validTipo =
      dvTipo && ["judicial", "administrativo", "extrajudicial", "procedimento"].includes(dvTipo)
        ? dvTipo
        : undefined;

    if (validTipo && defaultVinculoId) {
      setVinculoPanelOpen(true);
      setVinculoEnabled(true);
      setVinculoTipo(validTipo);
      setVinculoId(defaultVinculoId);
      void refreshProcessos(validTipo, defaultVinculoId);
    } else if (validTipo) {
      setVinculoPanelOpen(true);
      setVinculoEnabled(true);
      setVinculoTipo(validTipo);
      void refreshProcessos(validTipo);
    }

    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, user_id, nome, qualificacao")
        .eq("status", "ativo")
        .order("nome");
      setProfiles(sortProfiles((data || []) as Profile[]));

      if (user) {
        const me = (data || []).find((p: { user_id: string }) => p.user_id === user.id);
        if (me) setSenderName((me as Profile).nome);
      }
    };
    void load();
  }, [
    open,
    user,
    defaultRecipients,
    defaultSubject,
    defaultMessage,
    defaultVinculoTipo,
    defaultVinculoId,
    refreshProcessos,
  ]);

  const toggleRecipient = (userId: string) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const selectAll = () => {
    const allIds = profiles.filter((p) => p.user_id !== user?.id).map((p) => p.user_id);
    setSelectedIds(allIds);
  };

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    e.target.value = "";
    if (!list?.length) return;

    const incoming = Array.from(list);
    if (attachments.length + incoming.length > MAX_ANEXOS) {
      toast({
        title: "Limite de anexos",
        description: `Máximo de ${MAX_ANEXOS} arquivos por mensagem.`,
        variant: "destructive",
      });
      return;
    }

    const next: AttachmentItem[] = [...attachments];
    for (const f of incoming) {
      if (next.length >= MAX_ANEXOS) {
        toast({
          title: "Limite de anexos",
          description: `Máximo de ${MAX_ANEXOS} arquivos por mensagem.`,
          variant: "destructive",
        });
        break;
      }
      if (f.size > MAX_BYTES_ANEXO) {
        toast({
          title: "Arquivo muito grande",
          description: `Arquivo ${f.name} excede 20MB.`,
          variant: "destructive",
        });
        continue;
      }
      next.push({ id: crypto.randomUUID(), file: f });
    }
    setAttachments(next);
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const totalAnexosBytes = attachments.reduce((s, a) => s + a.file.size, 0);

  const selectedProcessoLabel = processosOpts.find((p) => p.id === vinculoId)?.label;

  const handleSend = async () => {
    if (!subject.trim() || !message.trim() || selectedIds.length === 0) {
      toast({
        title: "Preencha todos os campos e selecione ao menos um destinatário",
        variant: "destructive",
      });
      return;
    }
    if (vinculoEnabled && !vinculoId) {
      toast({
        title: "Vínculo incompleto",
        description: "Selecione um processo ou desative “Vincular a processo”.",
        variant: "destructive",
      });
      return;
    }

    const vinculoTipoFinal = vinculoEnabled && vinculoId ? vinculoTipo : undefined;
    const vinculoIdFinal = vinculoEnabled && vinculoId ? vinculoId : undefined;

    setSending(true);

    const precisaInsertComRetorno =
      attachments.length > 0 || Boolean(encaminharDeId);

    try {
      if (!precisaInsertComRetorno) {
        const { error } = await sendMessage({
          recipientIds: selectedIds,
          subject,
          message,
          senderName,
          vinculoTipo: vinculoTipoFinal,
          vinculoId: vinculoIdFinal,
        });
        if (error) {
          toast({ title: "Erro ao enviar", description: error.message, variant: "destructive" });
          return;
        }
        toast({ title: "Mensagem enviada!" });
        onOpenChange(false);
        return;
      }

      const rows: Database["public"]["Tables"]["user_messages"]["Insert"][] = selectedIds.map(
        (rid) => ({
          sender_id: user?.id ?? null,
          sender_name: senderName,
          recipient_id: rid,
          subject,
          message,
          message_type: "direct",
          reference_id: null,
          vinculo_tipo: vinculoTipoFinal ?? null,
          vinculo_id: vinculoIdFinal ?? null,
          encaminhada_de: encaminharDeId ?? null,
        }),
      );

      const { data: inserted, error: insErr } = await supabase
        .from("user_messages")
        .insert(rows)
        .select("id, thread_id");

      if (insErr) {
        toast({
          title: "Erro ao enviar",
          description: insErr.message,
          variant: "destructive",
        });
        return;
      }

      if (attachments.length > 0 && inserted?.length) {
        setUploadingAnexos(true);
        const folderKey = inserted[0].thread_id ?? inserted[0].id;
        const prefixo = `messages/${folderKey}`;

        const uploadedMeta: { key: string; nome: string; size: number; contentType: string }[] = [];
        for (const item of attachments) {
          const f = item.file;
          const key = await uploadAnexoCorreio(f, prefixo);
          if (!key) {
            toast({
              title: "Anexo não enviado",
              description: `Não foi possível enviar “${f.name}”. A mensagem foi entregue.`,
              variant: "default",
            });
            continue;
          }
          uploadedMeta.push({
            key,
            nome: f.name,
            size: f.size,
            contentType: f.type || "application/octet-stream",
          });
        }

        for (const row of inserted) {
          for (const meta of uploadedMeta) {
            const { error: attErr } = await supabase.from("user_message_attachments").insert({
              message_id: row.id,
              thread_id: row.thread_id,
              nome_arquivo: meta.nome,
              tamanho_bytes: meta.size,
              content_type: meta.contentType,
              r2_key: meta.key,
            });
            if (attErr) {
              toast({
                title: "Aviso",
                description: `Registro do anexo falhou: ${attErr.message}`,
                variant: "default",
              });
            }
          }
        }
        setUploadingAnexos(false);
      }

      toast({ title: "Mensagem enviada!" });
      onOpenChange(false);
    } finally {
      setSending(false);
      setUploadingAnexos(false);
    }
  };

  const filtered = profiles.filter(
    (p) => p.user_id !== user?.id && p.nome.toLowerCase().includes(search.toLowerCase()),
  );

  const qualLabels: Record<string, string> = {
    socio: "Sócio",
    associado: "Associado",
    estagiario: "Estagiário",
    colaborador: "Colaborador",
  };

  const busy = sending || uploadingAnexos;

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-[#791614]">
            <Send className="h-4 w-4" /> {dialogTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dialogTitle === "Responder"
              ? "Escreva a resposta e envie para o destinatário selecionado."
              : "Escolha destinatários, assunto e texto da mensagem interna."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipients */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Destinatários</Label>
              <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={selectAll}>
                <Users className="h-3 w-3" /> Todos
              </Button>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedIds.map((uid) => {
                  const p = profiles.find((pr) => pr.user_id === uid);
                  return (
                    <Badge
                      key={uid}
                      variant="secondary"
                      className="text-[10px] gap-1 pr-1 border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]"
                    >
                      {p?.nome || "Usuário"}
                      <X className="h-3 w-3 cursor-pointer" onClick={() => toggleRecipient(uid)} />
                    </Badge>
                  );
                })}
              </div>
            )}

            <Input
              id="busca-usuario"
              name="busca"
              placeholder="Buscar usuário..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-xs border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]"
            />
            <div className="max-h-[120px] overflow-y-auto border border-[rgba(169,171,174,0.35)] rounded-md divide-y bg-[#f9f6f0]">
              {filtered.map((p) => (
                <div
                  key={p.user_id}
                  className={cn(
                    "flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-muted/50 text-xs",
                    selectedIds.includes(p.user_id) ? "bg-[#791614]/5" : "",
                  )}
                  onClick={() => toggleRecipient(p.user_id)}
                >
                  <span>{p.nome}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {qualLabels[p.qualificacao] || p.qualificacao}
                  </span>
                </div>
              ))}
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">Nenhum usuário encontrado</p>
              )}
            </div>
          </div>

          {/* Vínculo a processo */}
          <Collapsible open={vinculoPanelOpen} onOpenChange={setVinculoPanelOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-md border border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] px-3 py-2 text-left hover:border-[rgba(215,179,105,0.55)] transition-colors"
              >
                <span className="font-accent text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                  Vincular a processo
                  <HelpTooltip text="Associa a mensagem a um processo judicial, administrativo, extrajudicial ou procedimento. Útil para rastrear o contexto no correio e em resumos." />
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-[#791614] transition-transform shrink-0",
                    vinculoPanelOpen ? "rotate-180" : "",
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="toggle-vinculo" className="text-xs cursor-pointer">
                  Associar a um processo
                </Label>
                <Switch
                  id="toggle-vinculo"
                  checked={vinculoEnabled}
                  onCheckedChange={(c) => {
                    setVinculoEnabled(c);
                    if (!c) setVinculoId("");
                    else void refreshProcessos(vinculoTipo, vinculoId || undefined);
                  }}
                />
              </div>
              {vinculoEnabled && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <Select
                      value={vinculoTipo}
                      onValueChange={(v) => {
                        const t = v as VinculoTipoDb;
                        setVinculoTipo(t);
                        setVinculoId("");
                        void refreshProcessos(t);
                      }}
                    >
                      <SelectTrigger className="h-9 text-xs border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]">
                        {(Object.keys(VINCULO_LABELS) as VinculoTipoDb[]).map((k) => (
                          <SelectItem key={k} value={k} className="text-xs">
                            {VINCULO_LABELS[k]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Processo</Label>
                    <Select
                      value={vinculoId || undefined}
                      onValueChange={setVinculoId}
                      disabled={loadingProcessos}
                    >
                      <SelectTrigger className="h-9 text-xs border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]">
                        <SelectValue placeholder={loadingProcessos ? "Carregando…" : "Selecione…"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)] max-h-[220px]">
                        {processosOpts.map((o) => (
                          <SelectItem key={o.id} value={o.id} className="text-xs">
                            {o.label}
                          </SelectItem>
                        ))}
                        {!loadingProcessos && processosOpts.length === 0 && (
                          <div className="px-2 py-2 text-[10px] text-muted-foreground">
                            Nenhum registro encontrado para este tipo.
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {vinculoId && selectedProcessoLabel && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-[rgba(121,22,20,0.25)] text-[#791614] bg-[#f9f6f0]"
                    >
                      {selectedProcessoLabel}
                    </Badge>
                  )}
                </>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Subject */}
          <div className="space-y-1">
            <Label className="text-xs">Assunto</Label>
            <Input
              id="assunto-mensagem"
              name="assunto"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto da mensagem"
              className="h-8 text-sm border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]"
            />
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label className="text-xs">Mensagem</Label>
            <Textarea
              id="corpo-mensagem"
              name="mensagem"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Escreva sua mensagem..."
              className="min-h-[100px] text-sm border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]"
            />
          </div>

          {/* Anexos */}
          <div className="space-y-2 rounded-md border border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Anexos</Label>
                <HelpTooltip text="Até 10 arquivos, 20MB cada. Os arquivos são enviados ao armazenamento seguro (R2) após o envio da mensagem." />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={onPickFiles}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-[10px] gap-1 border-[rgba(169,171,174,0.35)]"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || attachments.length >= MAX_ANEXOS}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Anexar arquivo
              </Button>
            </div>
            {attachments.length > 0 && (
              <p className="text-[10px] text-muted-foreground">
                Total: {formatBytes(totalAnexosBytes)} · {attachments.length}/{MAX_ANEXOS} arquivo(s)
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {attachments.map((a) => {
                const Icon = fileTypeIcon(a.file);
                return (
                  <div
                    key={a.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(169,171,174,0.45)] bg-[#f9f6f0]/80 px-2 py-1 text-[10px] max-w-full"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-[#791614]" />
                    <span className="truncate max-w-[180px]" title={a.file.name}>
                      {a.file.name}
                    </span>
                    <span className="text-muted-foreground shrink-0">{formatBytes(a.file.size)}</span>
                    <button
                      type="button"
                      className="shrink-0 rounded-full p-0.5 hover:bg-[#791614]/10"
                      aria-label="Remover anexo"
                      onClick={() => removeAttachment(a.id)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <Button onClick={handleSend} disabled={busy} className="w-full gap-2 bg-[#791614] hover:bg-[#5a0f0e]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {uploadingAnexos ? "Enviando anexos…" : sending ? "Enviando…" : "Enviar Mensagem"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComposeMessageDialog;
